import React from "react";
import { CheckCircle2, X, ArrowRight } from "lucide-react";
import "./ShipNow.css";

const equipmentOptions = [
  { id: "dry-van", label: "Dry Van" },
  { id: "reefer", label: "Reefer" },
  { id: "flatbed", label: "Flatbed" },
  { id: "other-equip", label: "Other" }
];

const frequencyOptions = [
  { id: "freq-once", label: "One-time" },
  { id: "freq-weekly", label: "Weekly" },
  { id: "freq-daily", label: "Daily" },
  { id: "freq-varies", label: "Varies" }
];

const ShipNowForm = ({ open, onClose }) => {
  const [status, setStatus] = React.useState({ type: "", message: "" });
  const [loading, setLoading] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [debugInfo, setDebugInfo] = React.useState("");
  const [formData, setFormData] = React.useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    pickupLocation: "",
    deliveryLocation: "",
    equipment: "",
    commodity: "",
    shipmentWeight: "",
    frequency: "",
    details: ""
  });

  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && open) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const resetForm = () => {
    setFormData({
      name: "",
      company: "",
      email: "",
      phone: "",
      pickupLocation: "",
      deliveryLocation: "",
      equipment: "",
      commodity: "",
      shipmentWeight: "",
      frequency: "",
      details: ""
    });
    setStatus({ type: "", message: "" });
    setShowSuccess(false);
    setDebugInfo("");
  };

  const handleClose = () => {
    onClose();
    setTimeout(resetForm, 300);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        FormType: "Shipper Quote",
        _subject: "New Shipper Quote Request",
        _replyto: formData.email,
        Name: formData.name,
        Company: formData.company,
        Email: formData.email,
        Phone: formData.phone,
        PickupLocation: formData.pickupLocation,
        DeliveryLocation: formData.deliveryLocation,
        Equipment: formData.equipment,
        Commodity: formData.commodity,
        ShipmentWeight: formData.shipmentWeight,
        Frequency: formData.frequency,
        Details: formData.details,
        Source: window.location.href,
        SubmittedAt: new Date().toISOString(),
        RawFormData: JSON.stringify(formData)
      };

      const formBody = new URLSearchParams();
      Object.entries(payload).forEach(([key, value]) => {
        formBody.append(key, value ?? "");
      });

      const response = await fetch("https://formspree.io/f/mgolkdry", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json"
        },
        body: formBody.toString()
      });

      let responseData = null;
      try {
        responseData = await response.json();
      } catch (parseError) {
        responseData = null;
      }

      if (response.ok && (!responseData || responseData.ok !== false)) {
        setStatus({ type: "success", message: "" });
        setShowSuccess(true);
        setDebugInfo(`Formspree OK (${response.status})`);
      } else {
        const message = responseData?.error || responseData?.message || "Failed to submit form";
        console.error("Formspree error (shipper):", response.status, responseData);
        setDebugInfo(`Formspree error (${response.status}): ${JSON.stringify(responseData)}`);
        throw new Error(message);
      }
    } catch (error) {
      setStatus({
        type: "error",
        message: "There was an error submitting your request. Please try again."
      });
      setDebugInfo(`Submit error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div
      className={`shipper-modal-overlay ${open ? "active" : ""}`}
      onClick={(e) => {
        if (e.target.classList.contains("shipper-modal-overlay")) {
          handleClose();
        }
      }}
    >
      <div className="shipper-modal">
        <div className="shipper-modal-header">
          <div className="shipper-modal-header-content">
            <h2>Get a Quote</h2>
            <p>Tell us about your shipment</p>
          </div>
          <button className="shipper-modal-close" type="button" onClick={handleClose}>
            <X />
          </button>
        </div>

        {!showSuccess && (
          <div className="shipper-form-state">
            <div className="shipper-modal-body">
              <div className="shipper-quick-benefits">
                <div className="shipper-quick-benefit">
                  <CheckCircle2 />
                  Quotes in 30 min
                </div>
                <div className="shipper-quick-benefit">
                  <CheckCircle2 />
                  98.5% on-time
                </div>
                <div className="shipper-quick-benefit">
                  <CheckCircle2 />
                  Real-time tracking
                </div>
              </div>

              {status.type === "error" && (
                <div className="shipper-form-error">
                  {status.message}
          </div>
        )}

              <form onSubmit={handleSubmit}>
                <div className="shipper-form-section-title">Contact Info</div>
                <div className="shipper-form-row">
                  <div className="shipper-form-group">
                    <label className="shipper-form-label">
                      Name <span className="required">*</span>
                    </label>
                    <input
                      className="shipper-form-input"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                required
                    />
                  </div>
                  <div className="shipper-form-group">
                    <label className="shipper-form-label">
                      Company <span className="required">*</span>
                    </label>
                    <input
                      className="shipper-form-input"
                      name="company"
                      value={formData.company}
                onChange={handleChange}
                      placeholder="Acme Manufacturing"
            required
                    />
                  </div>
                </div>

                <div className="shipper-form-row">
                  <div className="shipper-form-group">
                    <label className="shipper-form-label">
                      Email <span className="required">*</span>
                    </label>
                    <input
                      className="shipper-form-input"
                type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                required
                    />
                  </div>
                  <div className="shipper-form-group">
                    <label className="shipper-form-label">
                      Phone <span className="optional">(Optional)</span>
                    </label>
                    <input
                      className="shipper-form-input"
                      name="phone"
                      value={formData.phone}
                onChange={handleChange}
                      placeholder="(317) 555-1234"
                    />
                  </div>
                </div>

                <div className="shipper-form-section-title">Shipment Details</div>
                <div className="shipper-form-group">
                  <label className="shipper-form-label">
                    Lane <span className="required">*</span>
                  </label>
                  <div className="shipper-lane-input">
                    <input
                      className="shipper-form-input"
                name="pickupLocation"
                value={formData.pickupLocation}
                onChange={handleChange}
                      placeholder="Pickup city/zip"
                      required
                    />
                    <div className="shipper-lane-arrow">
                      <ArrowRight />
                    </div>
                    <input
                      className="shipper-form-input"
                name="deliveryLocation"
                value={formData.deliveryLocation}
                onChange={handleChange}
                      placeholder="Delivery city/zip"
                      required
                    />
                  </div>
                </div>

                <label className="shipper-equipment-label">
                  Equipment Needed <span className="required">*</span>
                </label>
                <div className="shipper-equipment-options">
                  {equipmentOptions.map((option) => (
                    <div className="shipper-equipment-option" key={option.id}>
                      <input
                        id={`shipper-${option.id}`}
                        type="radio"
                        name="equipment"
                        value={option.label}
                        checked={formData.equipment === option.label}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor={`shipper-${option.id}`}>{option.label}</label>
                    </div>
                  ))}
                </div>

                <div className="shipper-form-row">
                  <div className="shipper-form-group">
                    <label className="shipper-form-label">Commodity</label>
                    <input
                      className="shipper-form-input"
                name="commodity"
                value={formData.commodity}
                onChange={handleChange}
                      placeholder="What are you shipping?"
                    />
                  </div>
                  <div className="shipper-form-group">
                    <label className="shipper-form-label">Weight (lbs)</label>
                    <select
                      className="shipper-form-select"
                  name="shipmentWeight"
                  value={formData.shipmentWeight}
                  onChange={handleChange}
                    >
                      <option value="">Select weight</option>
                      <option value="0-10000">Under 10,000</option>
                      <option value="10000-20000">10,000 - 20,000</option>
                      <option value="20000-30000">20,000 - 30,000</option>
                      <option value="30000-40000">30,000 - 40,000</option>
                      <option value="40000+">40,000+</option>
                    </select>
                  </div>
                </div>

                <div className="shipper-form-group">
                  <label className="shipper-form-label">How often do you ship this lane?</label>
                  <div className="shipper-frequency-options">
                    {frequencyOptions.map((option) => (
                      <div className="shipper-frequency-option" key={option.id}>
                        <input
                          id={`shipper-${option.id}`}
                          type="radio"
                          name="frequency"
                          value={option.label}
                          checked={formData.frequency === option.label}
                          onChange={handleChange}
                        />
                        <label htmlFor={`shipper-${option.id}`}>{option.label}</label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="shipper-form-group">
                  <label className="shipper-form-label">
                    Additional Details <span className="optional">(Optional)</span>
                  </label>
                  <textarea
                    className="shipper-form-input shipper-textarea"
                    name="details"
                    value={formData.details}
                    onChange={handleChange}
                    placeholder="Pickup date, special requirements, or anything else we should know..."
                  />
                </div>

                <p className="shipper-consent-text">
                  By submitting, you consent to receive messages from Sunny Logistics regarding your shipment.
                  SMS rates may apply. See our <a href="/privacy">Privacy Policy</a> and <a href="/terms">Terms</a>.
                </p>

                <button type="submit" className="shipper-submit-btn" disabled={loading}>
                  {loading ? "Submitting..." : "Get My Quote →"}
                </button>
                {debugInfo && (
                  <p className="shipper-consent-text">Debug: {debugInfo}</p>
                )}
        </form>
            </div>

            <div className="shipper-modal-footer">
              <p>
                Need it faster? Call <a href="tel:+13175370657">(317) 537-0657</a>
              </p>
            </div>
          </div>
        )}

        {showSuccess && (
          <div className="shipper-success-state">
            <div className="shipper-success-icon">
              <CheckCircle2 />
            </div>
            <h3>Quote Request Received!</h3>
            <p>
              Thanks for reaching out. One of our team members will get back to you within 30 minutes during business hours.
            </p>
            <button type="button" className="shipper-success-btn" onClick={handleClose}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShipNowForm;